function buildMemory(connectors, answers) {
    return connectors
        .slice(0, answers.length)
        .map((connector, index) => `${connector} ${answers[index]}`.trim())
        .join('\n\n');
}

const DEMO_CHAPTERS = [
    {
        chapter: 'early-childhood',
        title: 'Early Childhood and Roots',
        memoryTitle: 'Early Childhood and Roots',
        isInitial: true,
        questions: [
            'What is your very first, earliest memory in life?',
            'Describe the house or apartment where you grew up. What was your favorite spot there?',
            'Tell me the most interesting or funny story you remember about your parents or grandparents.',
            'What did you love playing the most, either outside or at home, when you were a little child?',
            'What smell or taste instantly takes you back to your childhood (for example, grandma\'s pies or the scent of tangerines)?',
            'Do you know the story behind your name?',
            'Did harsh historical events such as war, post-war devastation, famine, or shortages affect your early childhood?',
            'What was your hometown or village like back when you were very little?',
            'All children have their fears. What were you most afraid of as a child?',
            'What were your favorite family traditions growing up?'
        ],
        buildMemory: (answers) => buildMemory([
            'My very first memory in life is this:',
            'The house where I grew up, and the spot I loved most in it:',
            'One story about my parents or grandparents I\'ve never forgotten:',
            'As a little child, more than anything, I loved this:',
            'There\'s a smell that instantly takes me back — it still does:',
            'There\'s a story behind my name, and it goes like this:',
            'Those early years weren\'t untouched by history:',
            'My hometown, back when I was very little, looked like this:',
            'Like every child, I had my fears, and mine was this:',
            'And the family tradition I loved most growing up was this:'
        ], answers)
    },
    {
        chapter: 'school-years',
        title: 'School Years',
        memoryTitle: 'School Years',
        isInitial: false,
        questions: [
            'Do you remember your very first day of school? How did you feel going there?',
            'Did you have a favorite teacher? What exactly made them memorable, and what did they teach you?',
            'Tell a story about a time you misbehaved or broke the rules at school. How were you punished?',
            'Who were your closest friends at school?',
            'Was there a time when you tried your hardest but suffered a bitter defeat or disappointment — in studies, sports, or a competition?',
            'What clubs, sports, or activities did you do after school?',
            'How did you usually spend your long summer vacations?',
            'Children can be cruel. Did you face bullying, mockery from peers, or the feeling of being an outcast at school?',
            'What do you remember eating at school — cafeteria food, snacks from home, or a favorite treat you always looked forward to?',
            'Which school event do you remember most vividly?'
        ],
        buildMemory: (answers) => buildMemory([
            'My very first day of school, I remember feeling this:',
            'There was a teacher I\'ll never forget:',
            'I wasn\'t always well-behaved — once, this happened:',
            'My closest friends back then were people like this:',
            'Not every effort ended in victory — I remember trying my hardest and still facing this:',
            'After school, I filled my time with this:',
            'Summers, for me, meant this:',
            'School wasn\'t always kind — I faced this:',
            'At school, I remember eating this:',
            'And one school event still stands out above the rest:'
        ], answers)
    },
    {
        chapter: 'youth-and-student-years',
        title: 'Youth and Student Years',
        memoryTitle: 'Youth and Student Years',
        isInitial: false,
        questions: [
            'What music did you listen to when you were young?',
            'Tell me about the moment you first felt truly independent from your parents.',
            'What was your funniest or craziest story from your student years (or youth)?',
            'Do you remember your very first teenage crush? How did you try to get that person\'s attention?',
            'Youth often involves tears. Describe your first difficult unrequited love or painful breakup. How did you cope then?',
            'What did you want to be when you grew up?',
            'Where did you usually hang out with friends?',
            'What event in your youth made you realize for the first time that the world can be dangerous and unfair, forcing you to grow up abruptly?',
            'How did you get money as a teenager — did you have a part-time job, an allowance, or a scholarship?',
            'Who did you genuinely admire during those years? Was it a famous actor, writer, athlete, or someone you knew?',
            'Did you go on student trips, camping, or hitchhiking? Tell me about your most vivid adventure.',
            'What significant historical or cultural events were happening then, and how were they discussed in your friend group?'
        ],
        buildMemory: (answers) => buildMemory([
            'Back then, the music that mattered to me was this:',
            'I remember the exact moment I first felt independent from my parents:',
            'One of the craziest, funniest stories from those years:',
            'I remember my very first crush, and how I tried to get their attention:',
            'Youth had its share of tears too — I remember a painful breakup like this:',
            'I dreamed of becoming this:',
            'My friends and I usually hung out here:',
            'There was a moment that made the world suddenly feel dangerous and unfair:',
            'As a teenager, I got money like this:',
            'There was someone I truly admired back then:',
            'One trip or adventure with friends still stands out:',
            'And the world around us was changing too — this is how we talked about it:'
        ], answers)
    },
    {
        chapter: 'early-adulthood',
        title: 'Early Adulthood',
        memoryTitle: 'Early Adulthood',
        isInitial: false,
        questions: [
            'What was your very first official job, and how much did you get paid?',
            'What did you spend your first real paycheck on?',
            'How did you choose your profession: was it a childhood dream, an accident, or someone\'s advice?',
            'What turned out to be the hardest part of independent adult life when you first moved out on your own?',
            'Was there a period of severe financial struggle at the beginning of your independent journey?',
            'What\'s something you saved up a long time to buy when you were first on your own? How did it feel to finally get it?',
            'What was your very first work team like? Did you manage to find true friends there?',
            'What\'s a funny (or embarrassing) mistake you made early in your career — the kind you can only really laugh about now?',
            'How did you relax after a long week at your first full-time job?',
            'Did you make a serious mistake or do something in your youth that you regretted for a long time afterward?'
        ],
        buildMemory: (answers) => buildMemory([
            'My very first official job, and what it paid, was this:',
            'My first real paycheck went toward this:',
            'I chose my profession this way:',
            'The hardest part of striking out on my own turned out to be this:',
            'There was a stretch of real financial struggle when I was starting out:',
            'There was something I saved up a long time to buy, and getting it felt like this:',
            'My very first work team was like this:',
            'There\'s a mistake from early in my career I can only laugh about now:',
            'After a long week at that first job, I unwound like this:',
            'There\'s a mistake from those years I regretted for a long time:'
        ], answers)
    },
    {
        chapter: 'starting-a-family',
        title: 'Starting a Family',
        memoryTitle: 'Starting a Family',
        isInitial: false,
        questions: [
            'Tell me the story of how you met your husband/wife. What was your very first impression of them?',
            'What was your first date like? How were you courted (or how did you court them)?',
            'Share a memory from your wedding day. Was there a funny or touching moment that you still remember?',
            'The path to starting a family is rarely cloudless. Did you face severe illnesses, struggles to have children?',
            'What did you feel when you first found out you were going to be a parent?',
            'What story from the first months of your first child\'s life stands out to you the most?',
            'How did your introduction to your future husband\'s or wife\'s parents go? Were you nervous?',
            'How did you choose names for your children? Did you and your spouse have long debates about it?',
            'Tell me about your first home as a married couple. How did you make it feel like your own?',
            'Was there a period or crisis in your marriage so severe that you were on the verge of divorce?'
        ],
        buildMemory: (answers) => buildMemory([
            'The story of how I met my husband/wife starts like this:',
            'Our first date, and how the courting went:',
            'One memory from our wedding day stayed with me:',
            'The path to starting our family wasn\'t without its struggles:',
            'The moment I found out I was going to be a parent, I felt this:',
            'One story from the first months of our first child\'s life stands out:',
            'Meeting my spouse\'s parents for the first time went like this:',
            'Choosing our children\'s names came down to this:',
            'Our first home as a married couple felt like this:',
            'There was a period in our marriage so hard we came close to divorce:'
        ], answers)
    },
    {
        chapter: 'maturity-and-career',
        title: 'Maturity and Career',
        memoryTitle: 'Maturity and Career',
        isInitial: false,
        questions: [
            'What professional achievement or success at work are you most proud of?',
            'How did you manage to balance work and household chores?',
            'Did you or someone very close to you face a severe illness during these years?',
            'What did a typical family weekend look like?',
            'Was there a mentor or a boss in your life who heavily influenced you?',
            'Looking back at those busy years, what gave you the most strength and joy?',
            'Was there a time when your life\'s work, career, or years of labor collapsed?',
            'Where did you usually go on family vacations?',
            'It is usually in maturity that we lose our parents. How did you cope with this loss?',
            'Did you ever have to radically change your profession, workplace, or lifestyle during your mature years? What pushed you to do it?',
            'Did you and your spouse have a close group of friends you socialized with?'
        ],
        buildMemory: (answers) => buildMemory([
            'The professional achievement I\'m proudest of is this:',
            'Balancing work and home, I managed it like this:',
            'During those years, illness touched us too:',
            'A typical family weekend looked like this:',
            'There was a mentor or a boss who shaped me deeply:',
            'What gave me the most strength and joy through those busy years:',
            'There was a time my life\'s work nearly collapsed:',
            'We usually spent our family vacations like this:',
            'Losing my parents during those years, I coped like this:',
            'There was a point I had to radically change course, and this is what pushed me:',
            'We had a close circle of friends we shared those years with:'
        ], answers)
    },
    {
        chapter: 'golden-years',
        title: 'The Golden Years',
        memoryTitle: 'The Golden Years',
        isInitial: false,
        questions: [
            'How did your life change after retirement? Did you pick up a new hobby?',
            'What has been the most painful or difficult part for you in the process of aging and losing your former physical strength?',
            'What main family tradition have you passed down to your children?',
            'If you could give just one piece of life advice to your 20-year-old self, what would you say?',
            'What brings you the greatest joy and peace today?',
            'How did your relationship with your own children change when they grew up and became independent?',
            'Describe your ideal, most pleasant day. What time does it start, and what little things does it consist of?',
            'Which modern invention (smartphones, video calls, the internet) surprised or pleased you the most when it appeared?',
            'Is there something on your wish list you\'ve always wanted to try or see, but never gotten around to?',
            'How would you like to be remembered by future generations? What three words would you use to describe yourself to them?'
        ],
        buildMemory: (answers) => buildMemory([
            'Life after retirement changed for me like this:',
            'The hardest part of growing older has been this:',
            'The family tradition I\'ve passed down to my children is this:',
            'If I could tell my 20-year-old self one thing, it would be this:',
            'What brings me the greatest joy and peace today is this:',
            'My relationship with my own children changed like this once they grew up:',
            'My ideal, most pleasant day looks like this:',
            'One modern invention that truly surprised or delighted me:',
            'There\'s still something on my list I haven\'t gotten to:',
            'And in the end, I\'d want to be remembered like this:'
        ], answers)
    }
];

export {
    DEMO_CHAPTERS
};
